import { memo, useEffect } from "react";
import { makeStyles } from "@mui/styles";
import { ZoomControl } from "react-mapbox-gl";
import { useDispatch, useSelector } from "react-redux";

import { PLAN_MANAGEMENT } from "../../consts/routes.js";
import { ALLOCATED, UNALLOCATED } from "../../consts/routeDetails";
import { SELECTED_ROUTE_NO_POSTCODES } from "../../consts/messages";
import { POSTCODE } from "../../consts/layers";
import {
  COLLECTIONS_PLAN_TYPE,
  LOCATIONS_PLAN_TYPE,
  TYPES_WITHOUT_TIMEBAND,
} from "../../consts/planTypes";

import diagonalLines from "../../assets/svg/diagonalLines.svg";

import { getBoundingBoxForRoute, routeHasTerritory } from "../../utils/routes";
import {
  processOuterConfig,
  processPostcodeConfig,
  processSectorConfig,
} from "../../utils/routeMatrix";
import { Postcode } from "../../utils/postcode";

import { useDepotMap } from "../../hooks/useDepotMap.js";

import {
  routesForPlanTypeSelector,
  changedOuterSelector,
  getMatrixProfileType,
  heatmapViewSelector,
  isEditOneModeSelector,
  routeConfigSelector,
  routePostcodesSelector,
  routeViewSelector,
  selectedOverlaySelector,
  updatedSelectedRouteSelector,
  volumesSinceLastReadyTimeSelector,
  redirectConfigSelector,
} from "../../reducers/matrixProfile/selectors";
import {
  allocatedPostcodesSelector,
  artificialPostcodeInEditSelector,
  lookupTableSelector,
  partialPostcodesSelector,
} from "../../reducers/depot/selectors";
import {
  fetchVolumeData,
  setRoutePostcodes,
} from "../../reducers/matrixProfile/thunkActions";
import {
  getFitBounds,
  getFitBoundsOptions,
} from "../../reducers/map/selectors";
import { setArtificialFeatures } from "../../reducers/map";
import {
  selectedRouteSelector,
  selectedTimeBandSelector,
  selectedCollectionTabSelector,
} from "../../reducers/ownApp/selectors";
import { setFitBounds } from "../../reducers/map/thunkActions";
import { addNotification } from "../../reducers/ownApp";
import { setRouteView } from "../../reducers/matrixProfile";

import { AppBar } from "../../components/AppBar";
import Copy from "../../components/Modals/Copy/Copy";
import { EditMapModal } from "../../components/Modals/EditMapModal/EditMapModal";
import { AddArtificialPostcodeModal } from "../../components/Modals/AddArtificialPostcode/AddArtificialPostcodeModal";
import { DriversLockedModal } from "../../components/Modals/DriversLocked/DriversLockedModal";
import { WeekendRouteOverlapModal } from "../../components/Modals/WeekendRouteOverlapModal/WeekendRouteOverlapModal";
import { EditCollectionsModal } from "../../components/Modals/EditCollections/EditCollectionsModal";
import { EditDriverModal } from "../../components/Modals/EditDriver/EditDriverModal";
import { EditOverridesModal } from "../../components/Modals/EditOverrides/EditOverridesModal";
import { EditPickupModal } from "../../components/Modals/EditPickup/EditPickupModal.jsx";
import RouteEditOptionsModal from "../../components/Modals/RouteEditOptionsModal/RouteEditOptionsModal";
import PickupEditOptionsModal from "../../components/Modals/PickupEditOptionsModal/PickupEditOptionsModal.jsx";
import OptimiseResultsModal from "../../components/Modals/OptimiseResultsModal/OptimiseResultsModal";
import { PostcodeInUseModal } from "../../components/Modals/PostcodeInUse/PostcodeInUseModal";
import FilterPanel from "../../components/FilterPanel";
import Header from "../../components/Header";
import { HeatmapLegend } from "../../components/HeatmapLegend";
import { HomeDepotController } from "../../components/HomeDepotController";
import { StreetViewController } from "../../components/StreetViewController";
import { Layers } from "../../components/Layers/Layers";
import { LoadingBackdrop } from "../../components/LoadingBackdrop";
import { MapLegend } from "../../components/MapLegend/index";
import Mapbox from "../../components/Mapbox";
import RouteEditBar from "../../components/RouteEditBar";
import { AppSections } from "../../components/LeftMenu";
import { Sources } from "../../components/Sources";
import Subheader from "../../components/Subheaders/PlanManagement/";
import { View } from "../../components/Base";
import { PlanManagementExpiredModal } from "../../components/Modals/PlanManagementExpired/PlanManagementExpiredModal";
import { PlanManagementReloadModal } from "../../components/Modals/PlanManagementReload/PlanManagementReloadModal";
import { NoCoordinatesLocation } from "../../components/Modals/NoCoordinatesLocation/NoCoordinatesLocation.jsx";

const useStyles = makeStyles({
  appContainer: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
  },
  content: {
    flexGrow: 1,
    position: "relative",
    display: "flex",
    overflow: "hidden",
    height: "calc(100% - 70px)",
  },
  left: {
    display: "flex",
  },
  right: {
    flexGrow: 1,
    position: "relative",
    display: "flex",
    flexDirection: "column-reverse",
  },
  mapContainer: {
    flexGrow: 1,
  },
});

const PlanManagement = memo(() => {
  const styles = useStyles();
  const lookupTables = useSelector(lookupTableSelector);
  const fitBounds = useSelector(getFitBounds);
  const fitBoundsOptions = useSelector(getFitBoundsOptions);
  const { outer } = useSelector(allocatedPostcodesSelector);
  const volumes = useSelector(volumesSinceLastReadyTimeSelector);
  const selectedRoute = useSelector(selectedRouteSelector);
  const routePostcodes = useSelector(routePostcodesSelector);
  const partialPostcodes = useSelector(partialPostcodesSelector);
  const selectedTimeBand = useSelector(selectedTimeBandSelector);
  const selectedOverlay = useSelector(selectedOverlaySelector);
  const isEditOneMode = useSelector(isEditOneModeSelector);
  const routeConfig = useSelector(routeConfigSelector);
  const redirectConfig = useSelector(redirectConfigSelector);
  const routes = useSelector(routesForPlanTypeSelector);
  const isHeatmapOn = useSelector(heatmapViewSelector);
  const changedOuter = useSelector(changedOuterSelector);
  const view = useSelector(routeViewSelector);
  const updatedSelectedRoute = useSelector(updatedSelectedRouteSelector);
  const isEditMode = useSelector(isEditOneModeSelector);
  const selectedType = useSelector(getMatrixProfileType);
  const collectionType = useSelector(selectedCollectionTabSelector);
  const artificialPostcodeInEdit = useSelector(
    artificialPostcodeInEditSelector
  );
  const dispatch = useDispatch();

  useEffect(() => {
    document.title = "Plan - Quantum Management";
  }, []);

  const { map, handleOnLoad } = useDepotMap();

  useEffect(() => {
    if (!map) return;
    let img = new Image(10, 10);
    img.onload = () => map.addImage("multi", img);
    img.src = diagonalLines;
  }, [map]);

  useEffect(() => {
    if (isEditMode) return;
    if (!selectedRoute && (!partialPostcodes || !lookupTables)) return;
    if (
      selectedRoute &&
      (!routePostcodes ||
        !routePostcodes?.POSTCODE[selectedRoute.routeCode] ||
        !lookupTables)
    )
      return;

    if (
      selectedRoute &&
      !routeHasTerritory(selectedRoute, selectedType, collectionType)
    ) {
      dispatch(
        addNotification({
          message: SELECTED_ROUTE_NO_POSTCODES,
          options: { variant: "info" },
        })
      );
      return;
    }

    const bbox =
      selectedRoute &&
      getBoundingBoxForRoute({
        route: selectedRoute.routeCode,
        routePostcodes,
        type: POSTCODE,
        lookupTables,
      });
    if (!bbox) return;
    dispatch(setFitBounds({ bbox }));
  }, [
    dispatch,
    isEditMode,
    lookupTables,
    partialPostcodes,
    routePostcodes,
    selectedRoute,
    selectedType,
    collectionType,
  ]);

  useEffect(() => {
    if (!updatedSelectedRoute) return;
    if (
      !routeHasTerritory(updatedSelectedRoute, selectedType, collectionType) &&
      view === ALLOCATED
    ) {
      dispatch(setRouteView(UNALLOCATED));
    } else if (
      routeHasTerritory(updatedSelectedRoute, selectedType, collectionType) &&
      view === UNALLOCATED
    ) {
      dispatch(setRouteView(ALLOCATED));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedSelectedRoute, selectedType, collectionType]);

  useEffect(() => {
    if (!map) return;
    const newArtificialFeatures = {};
    if (changedOuter) {
      processOuterConfig({
        map,
        outerConfig: { outer: changedOuter },
        reset: true,
        lookupTables,
        propagate: true,
        artificialFeatures: newArtificialFeatures,
      });
    } else {
      lookupTables &&
        lookupTables.OUTER &&
        Object.keys(lookupTables.OUTER).forEach((outer) => {
          processOuterConfig({
            map,
            outerConfig: { outer },
            reset: true,
            lookupTables,
            propagate: true,
            artificialFeatures: newArtificialFeatures,
          });
        });
      dispatch(setRoutePostcodes());
    }

    if (!routeConfig) return;

    const labelChanges = { OUTER: {}, SECTOR: {}, POSTCODE: {} };
    const multi = {};
    const postcodeToRoute = { OUTER: {}, SECTOR: {}, POSTCODE: {} };

    Array.isArray(routeConfig.outer) &&
      routeConfig.outer.length &&
      routeConfig.outer.forEach((outerConfig) => {
        if (changedOuter && outerConfig.outer !== changedOuter) return;
        if (volumes) {
          const postcode = outerConfig.outer;
          outerConfig.totalStops =
            !isNaN(volumes[postcode]?.stops) &&
            volumes[postcode]?.stops !== null
              ? volumes[postcode]?.stops
              : outerConfig.totalStops;
        }
        processOuterConfig({
          map,
          outerConfig,
          routes,
          labelChanges,
          lookupTables,
          multi,
          postcodeToRoute,
          heatmap: isHeatmapOn,
          propagate: !TYPES_WITHOUT_TIMEBAND.includes(selectedType),
          selectedTimeBand:
            !TYPES_WITHOUT_TIMEBAND.includes(selectedType) && selectedTimeBand,
          artificialFeatures: newArtificialFeatures,
        });
      });
    Array.isArray(routeConfig.sector) &&
      routeConfig.sector.length &&
      routeConfig.sector.forEach((sectorConfig) => {
        if (
          changedOuter &&
          new Postcode(sectorConfig.sector).toOuter() !== changedOuter
        )
          return;
        if (volumes) {
          const postcode = sectorConfig.sector;
          sectorConfig.totalStops =
            !isNaN(volumes[postcode]?.stops) &&
            volumes[postcode]?.stops !== null
              ? volumes[postcode]?.stops
              : sectorConfig.totalStops;
        }
        processSectorConfig({
          map,
          sectorConfig,
          redirectConfig,
          routes,
          labelChanges,
          lookupTables,
          multi,
          postcodeToRoute,
          heatmap: isHeatmapOn,
          propagate: !TYPES_WITHOUT_TIMEBAND.includes(selectedType),
          selectedTimeBand:
            !TYPES_WITHOUT_TIMEBAND.includes(selectedType) && selectedTimeBand,
          artificialFeatures: newArtificialFeatures,
        });
      });
    Array.isArray(routeConfig.postcode) &&
      routeConfig.postcode &&
      routeConfig.postcode.forEach((postcodeConfig) => {
        if (
          changedOuter &&
          new Postcode(postcodeConfig.postcode).toOuter() !== changedOuter
        )
          return;
        if (volumes) {
          const postcode = postcodeConfig.postcode;
          postcodeConfig.totalStops =
            !isNaN(volumes[postcode]?.stops) &&
            volumes[postcode]?.stops !== null
              ? volumes[postcode]?.stops
              : postcodeConfig.totalStops;
        }
        processPostcodeConfig({
          map,
          postcodeConfig,
          redirectConfig,
          routes,
          labelChanges,
          lookupTables,
          multi,
          postcodeToRoute,
          heatmap: isHeatmapOn,
          selectedTimeBand:
            !TYPES_WITHOUT_TIMEBAND.includes(selectedType) && selectedTimeBand,
          propagate: !TYPES_WITHOUT_TIMEBAND.includes(selectedType),
          artificialFeatures: newArtificialFeatures,
        });
      });
    dispatch(
      setRoutePostcodes({
        labelChanges,
        multi,
        postcodeToRoute,
        changedOuter: changedOuter,
      })
    );
    dispatch(setArtificialFeatures(newArtificialFeatures));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    isHeatmapOn,
    lookupTables,
    map,
    outer,
    routeConfig,
    redirectConfig,
    routes,
    selectedTimeBand,
    volumes,
  ]);

  useEffect(() => {
    selectedOverlay && dispatch(fetchVolumeData(selectedOverlay));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedOverlay]);

  return (
    <View className={styles.appContainer}>
      <LoadingBackdrop />

      <Header>
        <Subheader />
      </Header>
      <View className={styles.content}>
        <View className={styles.left}>
          <AppSections />
        </View>

        <View className={styles.right}>
          {(isEditOneMode || artificialPostcodeInEdit) && <RouteEditBar />}
          <View className={styles.mapContainer}>
            <Mapbox
              application={PLAN_MANAGEMENT}
              handleOnLoad={handleOnLoad}
              fitBounds={fitBounds}
              fitBoundsOptions={fitBoundsOptions}
            >
              <Sources map={map} />
              <Layers map={map} />
              <ZoomControl position={"bottom-right"} />
              <HomeDepotController />
              {selectedType !== LOCATIONS_PLAN_TYPE && <StreetViewController />}
              {isHeatmapOn && <HeatmapLegend />}
              {selectedType === COLLECTIONS_PLAN_TYPE && <MapLegend />}
            </Mapbox>
          </View>
          <AppBar />
        </View>
        <FilterPanel />
      </View>

      <NoCoordinatesLocation />
      <Copy />
      <EditMapModal />
      <AddArtificialPostcodeModal />
      <DriversLockedModal />
      <WeekendRouteOverlapModal />
      <EditCollectionsModal />
      <EditDriverModal />
      <EditOverridesModal />
      <EditPickupModal />
      <RouteEditOptionsModal />
      <PickupEditOptionsModal />
      <OptimiseResultsModal />
      <PostcodeInUseModal />
      <PlanManagementExpiredModal />
      <PlanManagementReloadModal />
    </View>
  );
});

export default PlanManagement;
